export enum AppCookie {
  LoginChallenge = 'login-challenge',
  TokenAdmin = 'alt-token-admin',
  TokenPartner = 'alt-token-partner',
  TokenCustomer = 'alt-token-customer',
  IdTokenHintCustomer = 'alt-id-token-hint-customer',
  RefreshAdmin = 'alt-refresh-admin',
  RefreshPartner = 'alt-refresh-partner',
  RefreshCustomer = 'alt-refresh-customer',
  RefreshLock = 'refresh-lock',
  PartnerId = 'X-Partner-Id',
  CustomerId = 'X-Customer-Id',
  PartnerIdAuth = 'partner-id-auth',
  Impersonating = 'impersonating',
  ImpersonationTarget = 'impersonation-target',
  ClosedBanner = 'closed-banner',
  LoggedAsGuest = 'logged-as-guest',
  PartnerActivationSetup = 'partner-activation-set-up',
  PartnerTimezone = 'partner-timezone',
  ClientTrendsChartHintDismissed = 'client-trends-chart-click-hint-dismissed',
  UtilizationChartHintDismissed = 'utilization-chart-click-hint-dismissed',
  OAuthClientOrigin = 'oauth-client-origin',
  AuthFlowRestarted = 'auth-flow-restarted',
  AuthFlowRestartPending = 'auth-flow-restart-pending',
  AuthFlowRestartCounted = 'auth-flow-restart-counted',
  // one-shot deep link preserved across the OAuth bounce when the access
  // token cookie is missing (report links open a cold tab)
  AuthReturnTo = 'auth-return-to',
  // the PKCE verifier, nonce and state of one Keycloak sign-in, held by the
  // auth app's /api/keycloak routes between start and callback (PLAT-1841)
  KeycloakSignIn = 'keycloak-sign-in',
  // the hash of a login challenge whose Hydra skip asked for a full sign-in,
  // so the form's own navigations do not run the skip check again (PLAT-1832)
  FullSignIn = 'full-sign-in',
  // the state and step of one trip to a Keycloak settings page, held by the
  // auth app's /api/keycloak routes (PLAT-1833)
  KeycloakSettings = 'keycloak-settings',
  // set on the Vercel auth app when the EKS copy sends a person back
  // (/api/eks-fallback), so /api/verification does not hand that person to
  // EKS again while it lives (PLAT-2118, T6-2)
  EksFallback = 'eks-fallback'
}

const HOUR = 60 * 60
const MONTH = 30 * 24 * HOUR
const DEFAULT_COOKIE_MAX_AGE = 6 * MONTH

const cookieMaxAge: Record<AppCookie, number> = {
  // set expiry to 2 hours (2x Ory default)
  [AppCookie.LoginChallenge]: 2 * HOUR,
  [AppCookie.TokenAdmin]: 2 * HOUR,
  [AppCookie.TokenPartner]: 2 * HOUR,
  [AppCookie.TokenCustomer]: 2 * HOUR,
  // set expiry to 2 months (2x Ory default)
  [AppCookie.RefreshAdmin]: 2 * MONTH,
  [AppCookie.RefreshPartner]: 2 * MONTH,
  [AppCookie.RefreshCustomer]: 2 * MONTH,
  // logout-only copy of the customer ID token. It must outlive the 2-hour
  // token cookie: Hydra accepts an expired id_token_hint, and without one it
  // drops post_logout_redirect_uri and sends the customer to its default
  // post-logout page — the partner app's /expired (FIRE-3568). Same TTL as
  // the refresh cookie, since a failed refresh is what triggers that logout
  [AppCookie.IdTokenHintCustomer]: 2 * MONTH,
  // set expiry to 10 seconds (request should be max 100ms, just to be safe if offline)
  [AppCookie.RefreshLock]: 10,
  // set expiry to default (6 months)
  [AppCookie.PartnerId]: DEFAULT_COOKIE_MAX_AGE,
  [AppCookie.CustomerId]: DEFAULT_COOKIE_MAX_AGE,
  [AppCookie.PartnerIdAuth]: DEFAULT_COOKIE_MAX_AGE,
  [AppCookie.Impersonating]: DEFAULT_COOKIE_MAX_AGE,
  // partner/customer id requested by /impersonate, held only until /callback
  // finishes the impersonator's own OAuth login (matches Hydra's 30-min
  // login challenge TTL)
  [AppCookie.ImpersonationTarget]: 30 * 60,
  [AppCookie.ClosedBanner]: DEFAULT_COOKIE_MAX_AGE,
  [AppCookie.LoggedAsGuest]: HOUR,
  [AppCookie.PartnerActivationSetup]: 24 * HOUR,
  [AppCookie.PartnerTimezone]: DEFAULT_COOKIE_MAX_AGE,
  [AppCookie.ClientTrendsChartHintDismissed]: DEFAULT_COOKIE_MAX_AGE,
  [AppCookie.UtilizationChartHintDismissed]: DEFAULT_COOKIE_MAX_AGE,
  // remembers which app initiated the OAuth flow so an expired login_challenge
  // can be restarted from there long after the 30-min Hydra TTL
  [AppCookie.OAuthClientOrigin]: DEFAULT_COOKIE_MAX_AGE,
  // loop brake: budgets auto-restart attempts (`<count>:<tier>`) per window,
  // prevents redirect loops while letting frozen-link re-clicks self-heal
  [AppCookie.AuthFlowRestarted]: 5 * 60,
  // recovery attribution (`<tier>:<surface>`): survives until the restarted
  // flow actually completes a login, unlike the loop brake above which is
  // cleared as soon as a valid login form renders. TTL matches Hydra's
  // 30-min challenge TTL so a much-later organic login isn't misattributed
  [AppCookie.AuthFlowRestartPending]: 30 * 60,
  // partner-domain dedupe for the restart arrival/recovery events: a single
  // expired-login rescue must count once, not once per refresh or back-nav
  [AppCookie.AuthFlowRestartCounted]: 5 * 60,
  // long enough to finish the OAuth bounce, short enough that a stale
  // deep link cannot hijack a later unrelated login
  [AppCookie.AuthReturnTo]: 10 * 60,
  // long enough to sign in at the realm with a password and a second factor
  [AppCookie.KeycloakSignIn]: 15 * 60,
  // Hydra's 30-min login challenge TTL
  [AppCookie.FullSignIn]: 30 * 60,
  // as long as a sign-in: the action asks for the password, and a second factor
  [AppCookie.KeycloakSettings]: 15 * 60,
  // one sign-in: long enough that a flow sent back never bounces to EKS
  [AppCookie.EksFallback]: 15 * 60
}

export const calculateExpires = (seconds: number) =>
  new Date(Date.now() + seconds * 1000)

export const getCookieExpires = (appCookie: AppCookie): Date => {
  const maxAge = cookieMaxAge[appCookie] || DEFAULT_COOKIE_MAX_AGE

  return calculateExpires(maxAge)
}

export type CookieSameSite = 'strict' | 'lax' | 'none'

export interface CookieOptions {
  expires?: Date
  httpOnly?: boolean
  secure?: boolean
  sameSite?: CookieSameSite
  path?: string
  domain?: string
}

// TLS terminates upstream in every deployed environment, so neither the
// `cookies` package nor js-cookie can infer Secure from the connection.
// Local http dev is the only case where Secure must be omitted, or the
// browser silently drops the cookie.
export const isSecureContext = (): boolean =>
  process.env.NODE_ENV === 'production'

const baseCookieOptions = (): Pick<CookieOptions, 'secure' | 'sameSite'> => ({
  secure: isSecureContext(),
  sameSite: 'lax'
})

export const cookieOptions = (
  appCookie: AppCookie,
  overrides: Partial<Omit<CookieOptions, 'secure'>> = {}
): CookieOptions => ({
  expires: getCookieExpires(appCookie),
  ...baseCookieOptions(),
  ...overrides
})

// For clearing: browsers only match/delete when the attributes line up
export const expireCookieOptions = (
  overrides: Partial<Omit<CookieOptions, 'secure'>> = {}
): CookieOptions => ({
  expires: new Date(0),
  ...baseCookieOptions(),
  ...overrides
})
